// import { login, logout } from "@/redux/authSlice";
// import store from "@/redux/store";
import axios, { AxiosResponse } from "axios";
import toast from "react-hot-toast";

// Helper function to get API base URL at runtime
function getApiBaseUrl(): string {
  // This will be evaluated at runtime, not build time
  if (typeof window !== "undefined" && window.API_URL) {
    return window.API_URL;
  }
  // Fallback - but this should never be hit if config loads properly
  console.warn("window.API_URL not set");
  return "";
}

// Configure Axios instance WITHOUT a baseURL - we'll set it dynamically
export const api = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to dynamically set baseURL from window.API_URL
// This ensures the baseURL is read at runtime, not build time
api.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically on each request
    config.baseURL = getApiBaseUrl();

    // Log for debugging (can be removed in production)
    if (!window.__API_BASE_LOGGED) {
      console.log("API using baseURL:", config.baseURL);
      window.__API_BASE_LOGGED = true;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Fetch Charity Data by ukCharityNumber to refresh TOKEN
export const refreshUserAuth = async (token: string | null) => {
  try {
    const response = await api.post(
      `/auth/refresh`,
      { token },
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching charity data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity data",
    );
  }
};

// NEW: Fetch user data WITHOUT refreshing token (for role sync)
export const getUserData = async (token: string | null) => {
  try {
    const response = await api.get(`/auth/me`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching user data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch user data",
    );
  }
};

//CHARITY-API
// Fetch Charity Data by ukCharityNumber
export const fetchCharityData = async (ukCharityNumber: string) => {
  try {
    const response = await api.get(`/charity/${ukCharityNumber}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching charity data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity data",
    );
  }
};

// ***********************************************************
// DONATION
// Fetch Donations by User ID
export const fetchDonationsByUser = async (
  userId: string | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`/donation/donor/${userId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    return response.data; // Return donations data to caller
  } catch (error: any) {
    console.error("Error fetching donations:", error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("No donations found for user:", userId);
      return [];
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch donations",
    );
  }
};

export const fetchDonationsByDonorOrg = async (
  donorOrgId: string | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`/donation/donor-org/${donorOrgId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    return response.data; // Return donations data to caller
  } catch (error: any) {
    console.error("Error fetching donations:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch donations",
    );
  }
};

// Fetch Donations by charity ID
export const fetchDonationsByCharity = async (
  charityId: string | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`donation/charity/${charityId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    return response.data; // Return donations data to caller
  } catch (error: any) {
    console.error("Error fetching donations:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch donations",
    );
  }
};

//For now lets have this two calls, there will be plans to delete the former soon
// Fetch Donations by CharityID
export const fetchDonationsByCharityOrg = async (
  charityId: string | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`donation/charity-org/${charityId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    return response.data; // Return donations data to caller
  } catch (error: any) {
    console.error("Error fetching donations:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch donations",
    );
  }
};

// Badges collectibles (real data: MultiversX NFTs + transaction badges; never labeled as NFT in UI)
// NFT image/amount/count come from MultiversX API (same as explorer): media[0].url, tags (transactionX, spent$Y)
export type DonorCollectiblesResponse = {
  nfts: Array<{
    id: string;
    name: string;
    description: string;
    imageUrl: string | null;
    imageUrls?: string[];
    tier?: string | null;
    patronRank?: number | null;
    identifier?: string;
    collection?: string;
    earned: boolean;
    rarity?: string;
    count?: number | null;
    totalSpent?: number | null;
    entityName?: string | null;
    explorerUrl?: string | null;
    txhash?: string | null;
  }>;
  transactionBadges: Array<{
    id: string;
    name: string;
    description: string;
    imageUrl: string | null;
    txhash: string;
    explorerUrl: string;
    dollaramount?: number;
    projectTitle?: string;
    earned: boolean;
    type: string;
    rarity?: string;
  }>;
};

export type ReferralStatsResponse = {
  successfulCount: number;
  referralCode: string;
  referralLink: string;
};

export const fetchReferralStats = async (token: string | null) => {
  try {
    const authHeader =
      token && !token.startsWith("Bearer ") ? `Bearer ${token}` : token;
    const response = await api.get<ReferralStatsResponse>(
      "/badges/referral-stats",
      {
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return { successfulCount: 0, referralCode: "", referralLink: "" };
    }
    console.error("Error fetching referral stats:", error);
    return { successfulCount: 0, referralCode: "", referralLink: "" };
  }
};

export const fetchDonorCollectibles = async (token: string | null) => {
  try {
    const response = await api.get<DonorCollectiblesResponse>(
      "/badges/collectibles/donor",
      {
        headers: { Authorization: token, "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return { nfts: [], transactionBadges: [] };
    }
    console.error("Error fetching donor collectibles:", error);
    return { nfts: [], transactionBadges: [] };
  }
};

export type CharityCollectiblesResponse = {
  nfts: Array<{
    id: string;
    name: string;
    description: string;
    imageUrl: string | null;
    imageUrls?: string[];
    tier?: string | null;
    patronRank?: number | null;
    identifier?: string;
    collection?: string;
    earned?: boolean;
    rarity?: string;
    count?: number | null;
    totalSpent?: number | null;
    entityName?: string | null;
    explorerUrl?: string | null;
    txhash?: string | null;
    dollaramount?: number;
    projectTitle?: string | null;
    transactionType?: "project" | "charity";
  }>;
  transactionBadges?: Array<{
    id: string;
    name: string;
    description: string;
    imageUrl: string | null;
    txhash: string;
    explorerUrl: string;
    dollaramount?: number;
    projectTitle?: string | null;
    transactionType?: "project" | "charity";
  }>;
};

export const fetchCharityCollectibles = async (
  charityId: string | null,
  token: string | null,
) => {
  if (!charityId) return { nfts: [], transactionBadges: [] };
  try {
    const response = await api.get<CharityCollectiblesResponse>(
      `/badges/collectibles/charity/${charityId}`,
      { headers: { Authorization: token, "Content-Type": "application/json" } },
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching charity collectibles:", error);
    return { nfts: [], transactionBadges: [] };
  }
};

// Fetch Donations by ProjectID
export const fetchDonationsByProject = async (
  projectId: string | number | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`donation/project/${projectId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    console.log("response.data -ABC", response.data);
    return response.data; // Return donations data to caller
  } catch (error: any) {
    console.error("Error fetching project donations:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project donations",
    );
  }
};


// ---------------------DONATION FEEDBACK API ------------------------------------

// export interface DonationFeedbackData {
//   id: number;
//   donationid: number;
//   experiencerating: number | null;
//   transparencyimpactrating: number | null;
//   suggestion: string | null;
//   donororgid: string | null;
//   tempuserid: string | null;
//   userid: string | null;
//   created_at: string;
// }

// export interface CharityFeedbackStats {
//   avg_experience: string | null;
//   avg_transparency_impact: string | null;
//   total_responses: string;
//   suggestions_count: string;
// }

// export interface CharityFeedbackStatsResponse {
//   charityId: string;
//   stats: CharityFeedbackStats;
// }

// export interface TransparencyAnalysisItem {
//   transparencyimpactrating: number;
//   avg_donation_usd: string;
//   response_count: string;
//   std_dev: string | null;
//   min_donation: string;
//   max_donation: string;
// }

// export interface TransparencyAnalysisResponse {
//   analysis: TransparencyAnalysisItem[];
// }

// export interface DonationFeedbackResponse {
//   feedback: DonationFeedbackData;
// }

// ═══════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════

export interface DonationFeedbackData {
  id: number;
  userid: number | null;
  tempuserid: string | null;
  donororgid: string | null;
  projectid: number | null;
  charityid: string | null;
  experiencerating: number | null;
  transparencyimpactrating: number | null;
  suggestion: string | null;
  created_at: string;
}

export interface DonationFeedbackResponse {
  feedback: DonationFeedbackData | DonationFeedbackData[];
}

export interface FeedbackListItem {
  id: number;
  projectid: number | null;
  charityid: string | null;
  experiencerating: number | null;
  transparencyimpactrating: number | null;
  suggestion: string | null;
  created_at: string;
  project_name: string | null;
  project_id: number | null;
  donor_firstname: string | null;
  donor_lastname: string | null;
  total_donated: string | null;
}

export interface FeedbackListProject {
  id: number;
  name: string;
}

export interface FeedbackListResponse {
  charityId: string;
  feedbacks: FeedbackListItem[];
  total: number;
  page: number;
  totalPages: number;
  projects: FeedbackListProject[];
}

export interface TransparencyAnalysisItem {
  transparencyimpactrating: number;
  avg_donation_usd: string;
  response_count: string;
  std_dev: string | null;
  min_donation: string;
  max_donation: string;
}

export interface TransparencyAnalysisResponse {
  analysis: TransparencyAnalysisItem[];
}

export interface CharityFeedbackStats {
  avg_experience: string | null;
  avg_transparency_impact: string | null;
  total_responses: string;
  suggestions_count: string;
}

export interface CharityFeedbackStatsResponse {
  charityId: string;
  stats: CharityFeedbackStats;
}

// ============ Types ============

// In your FeedbackListItem type, the date field name from the API is now:
export interface FeedbackListItem {
  id: number;
  donationid: number;
  experiencerating: number | null;
  transparencyimpactrating: number | null;
  suggestion: string | null;
  created_at: string;          // ← matches df.created_at
  donation_amount: number;
  donation_currency: string;
  dollaramount: number | null;
  donation_date: string;       // ← matches d.created_at as donation_date
  project_name: string | null; // ← nullable now (LEFT JOIN)
  project_id: number | null;   // ← nullable now (LEFT JOIN)
  donor_firstname: string | null;
  donor_lastname: string | null;
}

export interface FeedbackListProject {
  id: number;
  name: string;
}

export interface CharityFeedbackListResponse {
  charityId: string;
  feedbacks: FeedbackListItem[];
  total: number;
  page: number;
  totalPages: number;
  projects: FeedbackListProject[];
}

// ============ API Function ============

// export const getCharityFeedbackList = async (
//   charityId: string,
//   options: {
//     page?: number;
//     limit?: number;
//     projectId?: string;
//     rating?: string;
//   } = {},
// ): Promise<CharityFeedbackListResponse> => {
//   try {
//     const params = new URLSearchParams();
//     if (options.page) params.set("page", String(options.page));
//     if (options.limit) params.set("limit", String(options.limit));
//     if (options.projectId) params.set("projectId", options.projectId);
//     if (options.rating) params.set("rating", options.rating);

//     const queryString = params.toString();
//     const url = `/donation/donation-feedback/charity/${charityId}/list${queryString ? `?${queryString}` : ""}`;

//     const response = await api.get(url);
//     return response.data;
//   } catch (error: unknown) {
//     const err = error as {
//       response?: { data?: { error?: string; message?: string } };
//     };
//     console.error("Error fetching charity feedback list:", error);
//     throw new Error(
//       err?.response?.data?.error ||
//         err?.response?.data?.message ||
//         "Failed to fetch charity feedback list",
//     );
//   }
// };

// POST — Submit feedback (updated: projectId/charityId instead of donationId)
export const addDonationFeedback = async (data: {
  experienceRating: number | null;
  transparencyImpactRating: number | null;
  suggestion: string;
  projectId?: number | null;
  charityId?: string | null;
  donorOrgId: string | null;
  tempUserId: string | null;
  userId: string | number | null;
}) => {
  try {
    const response = await api.post("/donation/donation-feedback", data);
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error submitting donation feedback:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to submit feedback"
    );
  }
};

// GET — Check if feedback exists for user + entity
// Replaces old getDonationFeedbackByDonationId
export const checkFeedbackExists = async (params: {
  userId?: number | null;
  tempUserId?: string | null;
  projectId?: number | null;
  charityId?: string | null;
}): Promise<{ exists: boolean; feedback: DonationFeedbackData | null }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.set("userId", String(params.userId));
    if (params.tempUserId) queryParams.set("tempUserId", params.tempUserId);
    if (params.projectId) queryParams.set("projectId", String(params.projectId));
    if (params.charityId) queryParams.set("charityId", params.charityId);

    const response = await api.get(
      `/donation/donation-feedback/check?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { status?: number; data?: { error?: string } };
    };
    // 404 = no feedback exists — valid response
    if (err?.response?.status === 404) {
      return { exists: false, feedback: null };
    }
    console.error("Error checking feedback existence:", error);
    throw new Error(
      err?.response?.data?.error || "Failed to check feedback status"
    );
  }
};

// GET — Feedback by its own ID (replaces old getDonationFeedback)
export const getFeedbackById = async (
  feedbackId: number
): Promise<{ feedback: DonationFeedbackData }> => {
  try {
    const response = await api.get(
      `/donation/donation-feedback/${feedbackId}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error fetching feedback by ID:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to fetch feedback"
    );
  }
};

// GET — All feedback by authenticated user
export const getUserDonationFeedback = async (
  userId: number
): Promise<DonationFeedbackResponse> => {
  try {
    const response = await api.get(
      `/donation/donation-feedback/user/${userId}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error fetching user feedback:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to fetch feedback"
    );
  }
};

// GET — All feedback by temp/guest user
export const getTempUserDonationFeedback = async (
  tempUserId: string
): Promise<DonationFeedbackResponse> => {
  try {
    const response = await api.get(
      `/donation/donation-feedback/temp-user/${tempUserId}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error fetching temp user feedback:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to fetch feedback"
    );
  }
};

// GET — Charity aggregate stats
export const getCharityFeedbackStats = async (
  charityId: string
): Promise<CharityFeedbackStatsResponse> => {
  try {
    const response = await api.get(
      `/donation/donation-feedback/stats/charity/${charityId}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error fetching charity feedback stats:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to fetch charity feedback stats"
    );
  }
};

// GET — Charity feedback list (paginated, filterable)
export const getCharityFeedbackList = async (
  charityId: string,
  options?: {
    page?: number;
    limit?: number;
    projectId?: string;
    rating?: string;
  }
): Promise<FeedbackListResponse> => {
  try {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", String(options.page));
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.projectId) params.set("projectId", options.projectId);
    if (options?.rating) params.set("rating", options.rating);

    const response = await api.get(
      `/donation/donation-feedback/charity/${charityId}/list?${params.toString()}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    console.error("Error fetching charity feedback list:", error);
    throw new Error(
      err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to fetch feedback list"
    );
  }
};

// GET — Transparency impact analysis
export const getTransparencyImpactAnalysis =
  async (): Promise<TransparencyAnalysisResponse> => {
    try {
      const response = await api.get(
        "/donation/donation-feedback/analysis/transparency-impact"
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      console.error("Error fetching transparency analysis:", error);
      throw new Error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to fetch transparency analysis"
      );
    }
  }

export const getDonationFeedbackByDonationId = async (donationId: number): Promise<{ exists: boolean; feedback: DonationFeedbackResponse | null }> => {
  try {
    const response = await api.get(`/donation/donation-feedback/donation/${donationId}`);
    return response.data;

  } catch (error: unknown) {
    const err = error as {
      response?: { status?: number; data?: { error?: string } };
    };
    // 404 means no feedback exists — that's a valid "not found" response
    if (err?.response?.status === 404) {
      return { exists: false, feedback: null };
    }

    console.error("Error checking donation feedback:", error);
    throw new Error(
    err?.response?.data?.error || "Failed to check feedback status");
  }
};

// ************************************************************
// USER DATA API
// Fetch User Info by User ID
export const fetchUsers = async (
  userId: string | null,
  token: string | null,
) => {
  try {
    const response = await api.get(`/user/getuserinfo/${userId}`, {
      headers: { Authorization: token, "Content-Type": "application/json" },
    });
    return response.data?.data;
  } catch (error: any) {
    console.error("Error fetching user info:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch user info",
    );
  }
};

// export const updateUserAvatar = async ({
//   selectedFile,
//   userId,
//   token,
// }: {
//   selectedFile: File | null;
//   userId: string | null;
//   token: string | null;
// }): Promise<string | null> => {
//   if (!selectedFile) return null;

//   const formData = new FormData();
//   formData.append("file", selectedFile);
//   formData.append("userId", `${userId}`);

//   try {
//     const response = await api.patch(
//       `${window.API_URL}/user/update-avatar`,
//       formData,
//       {
//         headers: {
//           "Content-Type": "multipart/form-data",
//           Authorization: token,
//         },
//       },
//     );

//     console.log("Backend response:", response);

//     if (response.status === 200) {
//       console.log(response.data.avatarId);
//       return response.data.avatarId; // The URL of the uploaded image
//     } else {
//       return null;
//     }
//   } catch (error) {
//     console.error("File upload error:", error);
//     return null;
//   }
// };

// Update User
export const updateUserDetails = async (
  userDetails: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `${window.API_URL}/user/update`,
      userDetails,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data.user;
  } catch (error: any) {
    console.error("Error updating user details:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update user details. Please try again later.",
    );
  }
};

// Delete User
export const deleteUser = async ({
  userId,
  token,
}: {
  userId: string | null;
  token: string | null;
}): Promise<void> => {
  try {
    const response = await api.delete(
      `${window.API_URL}/user/deleteuser/${userId}`,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      },
    );

    if (response.status === 200) {
      console.log("User successfully deleted:", response.data.message);
    } else {
      console.error("Failed to delete user:", response.data);
    }
  } catch (error: any) {
    console.error("Error deleting user:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete user. Please try again later.",
    );
  }
};

// ********************************************************
//TRANSACTION API
// Fetch Transactions
export const fetchTransactions = async (token: string | null) => {
  try {
    const response = await api.get(`${window.API_URL}/transaction`, {
      headers: { Authorization: token },
    });
    return response.data?.projects; // Return projects or relevant data
  } catch (error: any) {
    console.error("Error fetching transactions:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch transactions",
    );
  }
};

// Fetch Transactions for a Charity
export const fetchTransactionsByCharity = async (
  ukCharityNumber: string,
  token: string | null,
) => {
  try {
    const response = await api.get(`${window.API_URL}/transaction/`, {
      data: { charityId: ukCharityNumber },
      headers: { Authorization: token },
    });
    return {
      charityName: response.data.charityname, // Charity name
      projects: response.data.projects || [], // Ensure projects is always an array
    };
  } catch (error: any) {
    console.error("Error fetching projects:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch projects. Please try again.",
    );
  }
};

// Fetch Transaction Data by Project ID
// Returns { transactions, nestedTransactions } where:
//   - transactions: flat array of all approved rows (includes branches with amount=0)
//   - nestedTransactions: tree with children[] and derived totals on branches
// Back-compat: if the server still returns a bare array (older deployments), we wrap it.
export const fetchProjectTransactions = async (projectId: string) => {
  try {
    const response = await api.get(`/transaction/project/${projectId}`);
    const data = response.data;
    if (Array.isArray(data)) {
      return { transactions: data, nestedTransactions: [] };
    }
    return data;
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project transaction data",
    );
  }
};

// ********************************************************
// PROJECT API
// Fetch Project Data by Project ID
export const fetchProjectData = async (
  projectId: string,
  token: string | null,
) => {
  try {
    const rawToken = token?.replace(/^Bearer\s/, "") || "";
    const response = await api.get(`/project/${projectId}`, {
      headers: {
        Authorization: rawToken, // This is what your backend expects
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project data",
    );
  }
};

// Fetch the one active campaign (if any) running for this project.
export const fetchProjectCampaign = async (
  projectId: string,
): Promise<import("@/models/project").PublicCampaign | null> => {
  try {
    const response = await api.get(`/project/${projectId}/campaign`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project campaign:", error);
    return null;
  }
};

export const fetchProjectsByCharityId = async (charityId: string) => {
  if (!charityId) throw new Error("Invalid charity ID");
  try {
    const response = await api.get(
      `/project/charity/${charityId}/projects-summary`,
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("No invites found for charity");
      return [];
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project data",
    );
  }
};

export interface ProjectSummaryPaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ProjectSummaryListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: "all" | "active" | "archived";
  sort?: "a-z" | "z-a";
}

/** Server-paginated, filtered and sorted project summaries - the Team > Projects tab's list. */
export const fetchProjectsPageByCharityId = async (
  charityId: string,
  params: ProjectSummaryListParams = {},
) => {
  if (!charityId) throw new Error("Invalid charity ID");
  try {
    const response = await api.get(
      `/project/charity/${charityId}/projects-summary`,
      { params: { page: 1, ...params } },
    );
    return response.data as { data: any[]; pagination: ProjectSummaryPaginationMeta };
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project data",
    );
  }
};

export const fetchCharityProjectsHealthStatus = async (charityId: string) => {
  if (!charityId) throw new Error("Invalid charity ID");
  try {
    const response = await api.get(
      `/project/health-status/charity/${charityId}`,
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project data",
    );
  }
};

//Toggle projectPublic status
export const toggleProjectPublicStatus = async (
  projectId: string,
  isPublic: boolean,
  token: string | null,
) => {
  if (!projectId) throw new Error("Invalid project ID");
  if (!token) throw new Error("No auth token provided");

  try {
    const response = await api.patch(
      `/project/${projectId}/toggle-public`,
      { isPublic },
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      },
    );

    // ✅ Check for success in the response
    if (response.data?.success === false) {
      throw new Error(
        response.data?.message || "Failed to update project visibility",
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("Error toggling project public status:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update project visibility",
    );
  }
};

export const createProject = async (projectData: any, token: string | null) => {
  try {
    // Assuming `token` is available and contains the authorization token
    // Or get the token from context/store

    const response = await api.post("/project", projectData, {
      headers: { Authorization: token },
    });

    return response.data;
  } catch (error: any) {
    console.error("❌ Error creating project:", error);

    if (error.response) {
      // Backend returned an error response
      const backendMessage =
        error.response.data?.error ||
        error.response.data?.message ||
        "Server returned an error while creating the project.";
      throw new Error(backendMessage);
    } else if (error.request) {
      // Request was sent but no response
      throw new Error("No response from the server. Please try again.");
    } else {
      // Something went wrong setting up the request
      throw new Error(error.message || "Unexpected error occurred.");
    }
  }
};

export const updateProject = async (
  projectId: any,
  updatedData: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(`/project/${projectId}`, updatedData, {
      headers: {
        Authorization: token, // Ensure token is sent for authentication
      },
    });
    return response.data;
  } catch (error: any) {
    if (error instanceof Error && (error as any).response) {
      console.error("Error updating project:", (error as any).response.data);
    }
    throw new Error(
      error.response.data.error ||
        error.response.data.message ||
        "Failed to update project",
    );
  }
};

// Add these alongside the existing deleteProject function

export const archiveProject = async (
  projectId: string,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/project/${projectId}/archive`,
      {},
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  } catch (error: any) {
    if (error instanceof Error && (error as any).response) {
      console.error("Error archiving project:", (error as any).response.data);
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to archive project",
    );
  }
};

export const restoreProject = async (
  projectId: string,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/project/${projectId}/restore`,
      {},
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  } catch (error: any) {
    if (error instanceof Error && (error as any).response) {
      console.error("Error restoring project:", (error as any).response.data);
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to restore project",
    );
  }
};

//Delete Projects
export const deleteProject = async (
  projectId: string,
  token: string | null,
) => {
  try {
    const response = await api.delete(`/project/${projectId}`, {
      headers: {
        Authorization: token, // Ensure token is sent for authentication
      },
    });
    return response.data;
  } catch (error: any) {
    if (error instanceof Error && (error as any).response) {
      console.error("Error deleting project:", (error as any).response.data);
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete project",
    );
  }
};

// Fetching project health data
export const fetchProjectHealthData = async (projectId: string) => {
  try {
    const response = await api.get(`/project/health-status`, {
      params: { projectId },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project data",
    );
  }
};

// ***************************************************************************************
// TEAM INVITE API
export const sendTeamInvite = async (values: any, token: string | null) => {
  try {
    const response = await api.post("/team-invite", JSON.stringify(values), {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
    return response.data; // Return the response data
  } catch (error: any) {
    console.error("Error sending team invite:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to send team invite",
    );
  }
};

// AUTH API: Login User
export const loginUser = async (values: {
  email: string;
  password: string;
}) => {
  try {
    const response = await api.post(`/auth/login`, JSON.stringify(values), {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Include cookies for cross-origin requests
    });
    return response.data; // Return the response data to the caller
  } catch (error: any) {
    // ✅ Don’t strip Axios’ error details — rethrow full error
    if (error.response) {
      throw error;
    }
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to log in",
    );
  }
};

//Google Sign -in
// AUTH API: Google Login
export const loginWithGoogle = async (idToken: string) => {
  try {
    const response = await api.post(
      `/auth/google`,
      JSON.stringify({ idToken }),
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true, // include cookies if your backend sets them
      },
    );

    return response.data; // { token, user }
  } catch (error: any) {
    console.error("Error logging in with Google:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to log in with Google",
    );
  }
};

// PROJECT API: Fetch Donations for Project
export const fetchProjectDonations = async (projectId: string) => {
  try {
    const response = await api.get(`/project/donations/${projectId}`);
    return response.data; // Return the donations data
  } catch (error: any) {
    console.error("Error fetching project donations:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project donations",
    );
  }
};

// Charity API
export const fetchCharityDetails = async (ukCharityNumber: string) => {
  try {
    const response = await api.get(`/charity/${ukCharityNumber}`);
    return response.data; // Return the charity data
  } catch (error: any) {
    console.error("Error fetching charity details:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity details",
    );
  }
};

// Function to verify and update the project password
export const verifyAndUpdatePassword = async (
  userId: string,
  currentPassword: string,
  newPassword: string,
): Promise<any> => {
  try {
    const response = await api.post(
      `${window.API_URL}/project/verify-password`,
      {
        userId,
        currentPassword,
        newPassword,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data; // Return the response data
  } catch (error: any) {
    console.error("Error verifying and updating password:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to verify and update password",
    );
  }
};

// Fetch user details
export const fetchUserDetails = async (token: string | null): Promise<any> => {
  try {
    const response = await api.get("/user/your-details", {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });
    return response.data; // Return the fetched user details
  } catch (error: any) {
    console.error("Error fetching user details:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch user details",
    );
  }
};

// Fetch charity data by charity number
export const getCharityData = async (charityNumber: string) => {
  try {
    const response = await api.get(`/charity/${charityNumber}`);
    return response.data; // Return charity data to the caller
  } catch (error: any) {
    console.error("Error fetching charity data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity data",
    );
  }
};

// Fetch donor data by charity number
export const getDonorData = async (charityNumber: string) => {
  try {
    const response = await api.get(`/donation/charity/${charityNumber}`);
    return response.data?.donors; // Return donors data to the caller
  } catch (error: any) {
    console.error("Error fetching donor data:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch donor data",
    );
  }
};

//  Uploads a file to the server.
export const uploadFile = async (
  token: string,
  formData: FormData,
): Promise<string | null> => {
  try {
    const response: AxiosResponse<{ url: string }> = await api.post(
      `${window.API_URL}/transaction/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      },
    );

    if (response.status === 200) {
      return response.data.url; // The URL of the uploaded image
    } else {
      toast.error("File upload failed. Please try again.");
      return null;
    }
  } catch (error: unknown) {
    console.error("File upload error:", error);
    toast.error(
      error instanceof Error
        ? error.message
        : "An error occurred while uploading the file.",
    );
    return null;
  }
};

//***********************************************************************************/
// PROJECT UPDATES API
// Fetch Project Updates
export const fetchProjectUpdates = async () => {
  try {
    const response = await api.get(`/project-update`);
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching project updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project updates",
    );
  }
};

export const fetchProjectUpdatesByProjectId = async (projectId: string) => {
  try {
    const response = await api.get(`/project-update/project/${projectId}`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching project updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project updates",
    );
  }
};

export const fetchProjectUpdatesByUpdateId = async (updateId: string) => {
  try {
    const response = await api.get(`/project-update/${updateId}`);
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching project updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project updates",
    );
  }
};

// Create a new project update
export const createProjectUpdates = async (
  projectUpdateData: any,
  token: string | null,
) => {
  try {
    const response = await api.post("/project-update", projectUpdateData, {
      headers: { Authorization: token },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error creating project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to create project update",
    );
  }
};

//Edit project updates
export const updateProjectUpdate = async (
  updateId: string,
  updatedData: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/project-update/${updateId}`,
      updatedData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated project update
  } catch (error: any) {
    console.error("Error updating project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update project update",
    );
  }
};

//Edit STATUS of project updates
export const updateProjectUpdateStatus = async (
  updateId: string,
  status: string,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/project-update/${updateId}/${status}`,
      { status },
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated project update
  } catch (error: any) {
    console.error("Error updating project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update project update",
    );
  }
};

//Delete project updates
export const deleteProjectUpdate = async (
  updateId: string,
  token: string | null,
) => {
  try {
    const response = await api.delete(`/project-update/${updateId}`, {
      headers: { Authorization: token },
    });

    return response.data; // Return the response message or data
  } catch (error: any) {
    console.error("Error deleting project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete project update",
    );
  }
};

//PROJECT-UPDATE-PHOTOS
//Fetch Project Updates Photos based on Project ID
export const fetchProjectUpdatesPhotosByProjectId = async (
  projectId: string,
) => {
  try {
    const response = await api.get(
      `/project-update-photos/project/${projectId}`,
    );
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching project updates photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project updates photos",
    );
  }
};

export const fetchProjectUpdatesPhotosByProjectUpdateId = async (
  projectUpdateId: string,
) => {
  try {
    const response = await api.get(
      `/project-update-photos/project-update/${projectUpdateId}`,
    );
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching project updates photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch project updates photos",
    );
  }
};

// Create a project update photos
export const addProjectUpdatesPhotos = async (
  projectPhotoData: any,
  token: string | null,
) => {
  try {
    const response = await api.post(
      "/project-update-photos",
      projectPhotoData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the created project update
  } catch (error: any) {
    console.error("Error creating project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to add project update photos",
    );
  }
};

//Edit project updates photos
export const updateProjectUpdatePhotos = async (
  updateId: string,
  updatedData: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/project-update-photos/${updateId}`,
      updatedData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated project update
  } catch (error: any) {
    console.error("Error updating project update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update project update",
    );
  }
};

//Delete project updates photos
export const deleteProjectUpdatePhotos = async (
  updateId: string,
  token: string | null,
) => {
  try {
    const response = await api.delete(`/project-update-photos/${updateId}`, {
      headers: { Authorization: token },
    });

    return response.data; // Return the response message or data
  } catch (error: any) {
    console.error("Error deleting project update photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete project update photos",
    );
  }
};

// ****************************************************************************************
// CHARITY UPDATES API
// Fetch Charity Updates
export const fetchCharityUpdates = async () => {
  try {
    const response = await api.get(`/charity-update`);
    return response.data; // Return charity updates data
  } catch (error: any) {
    console.error("Error fetching charity updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity updates",
    );
  }
};

//Fetch Charity Updates based on Project ID
export const fetchCharityUpdatesByCharityId = async (charityId: string) => {
  try {
    const response = await api.get(`/charity-update/charity/${charityId}`);
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching charity updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity updates",
    );
  }
};

export const fetchCharityUpdatesByUpdateId = async (updateId: string) => {
  try {
    const response = await api.get(`/charity-update/${updateId}`);
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching charity updates:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity updates",
    );
  }
};

// Create a new charity update
export const createCharityUpdates = async (
  charityUpdateData: any,
  token: string | null,
) => {
  try {
    const response = await api.post("/charity-update", charityUpdateData, {
      headers: { Authorization: token },
    });

    return response.data; // Return the created Charity update
  } catch (error: any) {
    console.error("Error creating charity update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to create charity update",
    );
  }
};

//Edit charity updates
export const updateCharityUpdate = async (
  updateId: string,
  updatedData: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/charity-update/${updateId}`,
      updatedData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated charity update
  } catch (error: any) {
    console.error("Error updating charity update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update charity update",
    );
  }
};

//Edit STATUS OF charity updates
export const updateCharityUpdateStatus = async (
  updateId: string,
  status: string,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/charity-update/${updateId}/${status}`,
      { status },
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated charity update
  } catch (error: any) {
    console.error("Error updating charity update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update charity update",
    );
  }
};

//Delete charity updates
export const deleteCharityUpdate = async (
  updateId: string,
  token: string | null,
) => {
  try {
    const response = await api.delete(`/charity-update/${updateId}`, {
      headers: { Authorization: token },
    });
    return response.data; // Return the response message or data
  } catch (error: any) {
    console.error("Error deleting charity update:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete charity update",
    );
  }
};

//CHARITY-UPDATE-PHOTOS
//Fetch Charity Updates based on Charity ID
export const fetchCharityUpdatesPhotosByCharityId = async (
  charityId: string,
) => {
  try {
    const response = await api.get(
      `/charity-update-photos/charity/${charityId}`,
    );
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching charity updates photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity updates photos",
    );
  }
};

export const fetchCharityUpdatesPhotosByCharityUpdateId = async (
  charityUpdateId: string,
) => {
  try {
    const response = await api.get(
      `/charity-update-photos/charity-update/${charityUpdateId}`,
    );
    return response.data; // Return project updates data
  } catch (error: any) {
    console.error("Error fetching charity updates photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch charity updates photos",
    );
  }
};

// Create a project update photos
export const addCharityUpdatesPhotos = async (
  charityPhotoData: any,
  token: string | null,
) => {
  try {
    const response = await api.post(
      "/charity-update-photos",
      charityPhotoData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the created project update
  } catch (error: any) {
    console.error("Error creating charity update photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to add charity update photos",
    );
  }
};

//Edit charity updates photos
export const updateCharityUpdatePhotos = async (
  updateId: string,
  updatedData: any,
  token: string | null,
) => {
  try {
    const response = await api.patch(
      `/charity-update-photos/${updateId}`,
      updatedData,
      {
        headers: { Authorization: token },
      },
    );

    return response.data; // Return the updated project update
  } catch (error: any) {
    console.error("Error updating charity update photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update charity update photos",
    );
  }
};

//Delete project updates
export const deleteCharityUpdatePhotos = async (
  updateId: string,
  token: string | null,
) => {
  try {
    const response = await api.delete(`/charity-update-photos/${updateId}`, {
      headers: { Authorization: token },
    });

    return response.data; // Return the response message or data
  } catch (error: any) {
    console.error("Error deleting charity update photos:", error);
    throw new Error(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete charity update photos",
    );
  }
};
